import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './profile-page.style.js';
import { authService, AuthUser } from '../services/auth.service.js';
import { db } from '../services/auth.service.js';
import { getUserByUid, updateUser, getCompanyById, updateCompany } from '@cmms/api';
import { signInWithEmailAndPassword, updatePassword } from 'firebase/auth';
import { firebaseService } from '../services/firebase.service.js';
import { notificationService } from '../services/notification.service.js';
import './notification-settings/notification-settings.js';

interface ProfileFormData {
  name: string;
  email: string;
  phone_number: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
  created_at: number;
}

interface CompanyFormData {
  name: string;
  legal_name: string;
  industry: string;
  company_size: number;
  founded_year: number | null;
  street_name: string;
  house_number: string;
  house_number_addition: string | null;
  zipcode: string;
  city: string;
  state_province: string;
  country: string;
  phone_number: string;
  whatsapp_number: string | null;
  email_address: string;
  website_url: string | null;
  vat_number: string | null;
  tax_id: string | null;
  iban: string | null;
  billing_email: string;
  billing_cycle: 'Monthly' | 'Yearly';
  timezone: string;
  currency: string;
  language: string;
  date_format: string;
  work_hours_start: string;
  work_hours_end: string;
  default_priority: 'Low' | 'Medium' | 'High' | 'Critical';
  auto_assign_enabled: boolean;
}

@customElement('profile-page')
export class ProfilePage extends LitElement {
  static styles = styles;

  @state() private user: AuthUser | null = null;
  @property({ type: String }) companyId: string = '';
  @state() private companyName: string = 'Loading...';
  @state() private formData: ProfileFormData = {
    name: '',
    email: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zipcode: '',
    created_at: 0
  };
  @state() private isLoading = false;
  @state() private errorMessage = '';
  @state() private successMessage = '';
  @state() private validationErrors: Record<string, string> = {};
  @state() private showChangePassword = false;
  @state() private passwordData = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  };
  @state() private companyFormData: CompanyFormData = {
    name: '',
    legal_name: '',
    industry: '',
    company_size: 0,
    founded_year: null,
    street_name: '',
    house_number: '',
    house_number_addition: null,
    zipcode: '',
    city: '',
    state_province: '',
    country: '',
    phone_number: '',
    whatsapp_number: null,
    email_address: '',
    website_url: null,
    vat_number: null,
    tax_id: null,
    iban: null,
    billing_email: '',
    billing_cycle: 'Monthly',
    timezone: 'Europe/Amsterdam',
    currency: 'EUR',
    language: 'nl',
    date_format: 'DD/MM/YYYY',
    work_hours_start: '08:00',
    work_hours_end: '17:00',
    default_priority: 'Medium',
    auto_assign_enabled: false
  };
  @state() private companyValidationErrors: Record<string, string> = {};
  @state() private originalFormData: ProfileFormData = {
    name: '',
    email: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zipcode: '',
    created_at: 0
  };
  @state() private originalCompanyFormData: CompanyFormData = {
    name: '',
    legal_name: '',
    industry: '',
    company_size: 0,
    founded_year: null,
    street_name: '',
    house_number: '',
    house_number_addition: null,
    zipcode: '',
    city: '',
    state_province: '',
    country: '',
    phone_number: '',
    whatsapp_number: null,
    email_address: '',
    website_url: null,
    vat_number: null,
    tax_id: null,
    iban: null,
    billing_email: '',
    billing_cycle: 'Monthly',
    timezone: 'Europe/Amsterdam',
    currency: 'EUR',
    language: 'nl',
    date_format: 'DD/MM/YYYY',
    work_hours_start: '08:00',
    work_hours_end: '17:00',
    default_priority: 'Medium',
    auto_assign_enabled: false
  };

  private unsubscribeAuth?: () => void;

  connectedCallback() {
    super.connectedCallback();
    
    // Listen for auth state changes
    this.unsubscribeAuth = authService.onAuthStateChanged((user) => {
      this.user = user;
      if (user) {
        this.loadUserProfile();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }
  }

  private async loadUserProfile() {
    if (!this.user?.uid) return;

    try {
      this.isLoading = true;
      const result = await getUserByUid(db, this.user.uid);
      
      if (result.data) {
        const userData = result.data as any;
        this.formData = {
          name: userData.model?.name || this.user.displayName || '',
          email: userData.model?.email || this.user.email || '',
          phone_number: userData.model?.phone_number || '',
          address: userData.model?.address || '',
          city: userData.model?.city || '',
          state: userData.model?.state || '',
          country: userData.model?.country || '',
          zipcode: userData.model?.zipcode || '',
          created_at: userData.model?.created_at || 0
        };
        
        // Store original data for change tracking
        this.originalFormData = { ...this.formData };
        
        // Load company name if company ID is available
        if (userData.model?.company_id) {
          this.companyId = userData.model.company_id;
          await this.loadCompanyName();
          await this.loadCompanyData();
        }

        // Initialize notification service for this user
        await notificationService.initialize(this.user.uid);
      } else {
        // Fallback to auth user data
        this.formData = {
          name: this.user.displayName || '',
          email: this.user.email || '',
          phone_number: '',
          address: '',
          city: '',
          state: '',
          country: '',
          zipcode: '',
          created_at: 0
        };
        
        // Try to load company name from auth user
        if (this.user.companyId) {
          this.companyId = this.user.companyId;
          await this.loadCompanyName();
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      this.errorMessage = 'Failed to load profile data';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadCompanyName() {
    if (!this.companyId) {
      this.companyName = 'N/A';
      return;
    }
    
    try {
      const result = await getCompanyById(db, this.companyId);
      if (result.data && (result.data as any).model) {
        this.companyName = (result.data as any).model.name || 'Unknown Company';
      } else if (result.error) {
        console.error('Company API error:', result.error);
        this.companyName = 'Unknown Company';
      } else {
        this.companyName = 'Unknown Company';
      }
    } catch (error) {
      console.error('Error loading company name:', error);
      this.companyName = 'Unknown Company';
    }
  }

  private async loadCompanyData() {
    if (!this.companyId) return;

    try {
      const result = await getCompanyById(db, this.companyId);
      if (result.data && (result.data as any).model) {
        const companyData = (result.data as any).model;
        this.companyFormData = {
          name: companyData.name || '',
          legal_name: companyData.legal_name || '',
          industry: companyData.industry || '',
          company_size: companyData.company_size || 0,
          founded_year: companyData.founded_year || null,
          street_name: companyData.street_name || '',
          house_number: companyData.house_number || '',
          house_number_addition: companyData.house_number_addition || null,
          zipcode: companyData.zipcode || '',
          city: companyData.city || '',
          state_province: companyData.state_province || '',
          country: companyData.country || '',
          phone_number: companyData.phone_number || '',
          whatsapp_number: companyData.whatsapp_number || null,
          email_address: companyData.email_address || '',
          website_url: companyData.website_url || null,
          vat_number: companyData.vat_number || null,
          tax_id: companyData.tax_id || null,
          iban: companyData.iban || null,
          billing_email: companyData.billing_email || '',
          billing_cycle: companyData.billing_cycle || 'Monthly',
          timezone: companyData.timezone || 'Europe/Amsterdam',
          currency: companyData.currency || 'EUR',
          language: companyData.language || 'nl',
          date_format: companyData.date_format || 'DD/MM/YYYY',
          work_hours_start: companyData.work_hours_start || '08:00',
          work_hours_end: companyData.work_hours_end || '17:00',
          default_priority: companyData.default_priority || 'Medium',
          auto_assign_enabled: companyData.auto_assign_enabled || false
        };
        
        // Store original company data for change tracking
        this.originalCompanyFormData = { ...this.companyFormData };
      }
    } catch (error) {
      console.error('Error loading company data:', error);
      this.errorMessage = 'Failed to load company data';
    }
  }

  private validateForm(): boolean {
    this.validationErrors = {};

    if (!this.formData.name.trim()) {
      this.validationErrors.name = 'Name is required';
    }

    if (!this.formData.email.trim()) {
      this.validationErrors.email = 'Email is required';
    } else if (!this.isValidEmail(this.formData.email)) {
      this.validationErrors.email = 'Please enter a valid email address';
    }

    // Phone number is optional but if provided should be valid
    if (this.formData.phone_number && !this.isValidPhone(this.formData.phone_number)) {
      this.validationErrors.phone_number = 'Please enter a valid phone number';
    }

    // Address fields validation (all optional)
    if (this.formData.zipcode && !this.isValidZipcode(this.formData.zipcode)) {
      this.validationErrors.zipcode = 'Please enter a valid zip/postal code';
    }

    return Object.keys(this.validationErrors).length === 0;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private isValidPhone(phone: string): boolean {
    // Basic phone validation - allows various formats
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  }

  private isValidZipcode(zipcode: string): boolean {
    // Basic zipcode validation - allows US zipcodes (5 digits) and extended (5-4 format)
    const zipcodeRegex = /^\d{5}(-\d{4})?$/;
    return zipcodeRegex.test(zipcode);
  }

  private handleInputChange(e: Event, field: keyof ProfileFormData) {
    const target = e.target as any;
    const value = target.value;
    
    this.formData = {
      ...this.formData,
      [field]: value
    };

    // Clear validation error for this field
    if (this.validationErrors[field]) {
      delete this.validationErrors[field];
      this.validationErrors = { ...this.validationErrors };
    }

    // Clear success message when user starts editing
    if (this.successMessage) {
      this.successMessage = '';
    }
  }

  private async handleSave() {
    // Validate profile form if there are profile changes
    if (this.hasProfileChanges() && !this.validateForm()) {
      return;
    }

    // Validate company form if there are company changes
    if (this.hasCompanyChanges() && !this.validateCompanyForm()) {
      return;
    }

    if (!this.user?.uid) {
      this.errorMessage = 'User not authenticated';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const promises = [];

      // Save profile changes if any
      if (this.hasProfileChanges()) {
        promises.push(
          updateUser(db, this.user.uid, {
            name: this.formData.name,
            phone_number: this.formData.phone_number,
            address: this.formData.address,
            city: this.formData.city,
            state: this.formData.state,
            country: this.formData.country,
            zipcode: this.formData.zipcode,
            company_id: this.companyId,
          })
        );
      }

      // Save company changes if any and user is admin/super admin
      if (this.hasCompanyChanges() && this.isAdminOrSuperAdmin() && this.companyId) {
        promises.push(updateCompany(db, this.companyId, this.companyFormData));
      }

      const results = await Promise.all(promises);
      
      // Check if all saves were successful
      const allSuccessful = results.every(result => result.data);
      
      if (allSuccessful) {
        // Update original data to reflect saved state
        if (this.hasProfileChanges()) {
          this.originalFormData = { ...this.formData };
          // Update auth service user data
          if (this.user) {
            this.user.displayName = this.formData.name;
          }
        }
        
        if (this.hasCompanyChanges() && this.isAdminOrSuperAdmin()) {
          this.originalCompanyFormData = { ...this.companyFormData };
          // Update company name if it was changed
          this.companyName = this.companyFormData.name;
        }

        const changeTypes = [];
        if (this.hasProfileChanges()) changeTypes.push('profile');
        if (this.hasCompanyChanges() && this.isAdminOrSuperAdmin()) changeTypes.push('company');
        
        this.successMessage = `${changeTypes.join(' and ')} updated successfully`;
      } else {
        // Find the first error
        const errorResult = results.find(result => !result.data);
        this.errorMessage = errorResult?.error || 'Failed to save changes';
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      this.errorMessage = 'Failed to save changes';
    } finally {
      this.isLoading = false;
    }
  }

  private handleChangePasswordClick() {
    this.showChangePassword = true;
    this.passwordData = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    this.errorMessage = '';
    this.successMessage = '';
  }

  private handlePasswordInputChange(e: Event, field: keyof typeof this.passwordData) {
    const target = e.target as any;
    this.passwordData = {
      ...this.passwordData,
      [field]: target.value
    };
    
    // Clear validation errors when user starts typing
    if (this.validationErrors[field]) {
      delete this.validationErrors[field];
      this.validationErrors = { ...this.validationErrors };
    }
  }

  private handleCompanyInputChange(e: Event, field: keyof CompanyFormData) {
    const target = e.target as any;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    
    this.companyFormData = {
      ...this.companyFormData,
      [field]: value
    };

    // Clear validation error for this field
    if (this.companyValidationErrors[field]) {
      delete this.companyValidationErrors[field];
      this.companyValidationErrors = { ...this.companyValidationErrors };
    }

    // Clear success message when user starts editing
    if (this.successMessage) {
      this.successMessage = '';
    }
  }

  private validatePasswordForm(): boolean {
    this.validationErrors = {};

    if (!this.passwordData.currentPassword.trim()) {
      this.validationErrors.currentPassword = 'Current password is required';
    }

    if (!this.passwordData.newPassword.trim()) {
      this.validationErrors.newPassword = 'New password is required';
    } else if (this.passwordData.newPassword.length < 6) {
      this.validationErrors.newPassword = 'New password must be at least 6 characters';
    }

    if (!this.passwordData.confirmPassword.trim()) {
      this.validationErrors.confirmPassword = 'Please confirm your new password';
    } else if (this.passwordData.newPassword !== this.passwordData.confirmPassword) {
      this.validationErrors.confirmPassword = 'Passwords do not match';
    }

    return Object.keys(this.validationErrors).length === 0;
  }

  private async handlePasswordSave() {
    if (!this.validatePasswordForm()) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Use Firebase Auth functions
      const auth = firebaseService.getAuth();

      if (!this.user?.email) {
        throw new Error('User email not found');
      }

      // Re-authenticate user with current password
      await signInWithEmailAndPassword(auth, this.user.email, this.passwordData.currentPassword);
      
      // Update password
      if (auth.currentUser) {
        await updatePassword(auth.currentUser, this.passwordData.newPassword);
        
        this.successMessage = 'Password updated successfully';
        this.showChangePassword = false;
        this.passwordData = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        };
      } else {
        throw new Error('User not authenticated');
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        this.errorMessage = 'Current password is incorrect';
      } else if (error.code === 'auth/weak-password') {
        this.errorMessage = 'New password is too weak';
      } else {
        this.errorMessage = error.message || 'Failed to update password';
      }
    } finally {
      this.isLoading = false;
    }
  }

  private handlePasswordCancel() {
    this.showChangePassword = false;
    this.passwordData = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    this.validationErrors = {};
    this.errorMessage = '';
    this.successMessage = '';
  }

  private validateCompanyForm(): boolean {
    this.companyValidationErrors = {};

    if (!this.companyFormData.name.trim()) {
      this.companyValidationErrors.name = 'Company name is required';
    }

    if (!this.companyFormData.legal_name.trim()) {
      this.companyValidationErrors.legal_name = 'Legal name is required';
    }

    if (!this.companyFormData.industry.trim()) {
      this.companyValidationErrors.industry = 'Industry is required';
    }

    if (!this.companyFormData.street_name.trim()) {
      this.companyValidationErrors.street_name = 'Street name is required';
    }

    if (!this.companyFormData.house_number.trim()) {
      this.companyValidationErrors.house_number = 'House number is required';
    }

    if (!this.companyFormData.zipcode.trim()) {
      this.companyValidationErrors.zipcode = 'Zipcode is required';
    }

    if (!this.companyFormData.city.trim()) {
      this.companyValidationErrors.city = 'City is required';
    }

    if (!this.companyFormData.state_province.trim()) {
      this.companyValidationErrors.state_province = 'State/Province is required';
    }

    if (!this.companyFormData.country.trim()) {
      this.companyValidationErrors.country = 'Country is required';
    }

    if (!this.companyFormData.phone_number.trim()) {
      this.companyValidationErrors.phone_number = 'Phone number is required';
    }

    if (!this.companyFormData.email_address.trim()) {
      this.companyValidationErrors.email_address = 'Email address is required';
    } else if (!this.isValidEmail(this.companyFormData.email_address)) {
      this.companyValidationErrors.email_address = 'Please enter a valid email address';
    }

    if (!this.companyFormData.billing_email.trim()) {
      this.companyValidationErrors.billing_email = 'Billing email is required';
    } else if (!this.isValidEmail(this.companyFormData.billing_email)) {
      this.companyValidationErrors.billing_email = 'Please enter a valid email address';
    }

    if (this.companyFormData.founded_year && (this.companyFormData.founded_year < 1800 || this.companyFormData.founded_year > new Date().getFullYear())) {
      this.companyValidationErrors.founded_year = 'Founded year must be between 1800 and current year';
    }

    if (this.companyFormData.company_size < 0) {
      this.companyValidationErrors.company_size = 'Company size must be a positive number';
    }

    return Object.keys(this.companyValidationErrors).length === 0;
  }


  private getMemberSinceDate(): string {
    // Use created_at from user model first (from formData), then fallback to auth user
    const createdTimestamp = this.formData.created_at || this.user?.created_at;
    
    if (createdTimestamp && createdTimestamp > 0) {
      return new Date(createdTimestamp).toLocaleDateString();
    }
    
    return 'N/A';
  }

  private getRoleDisplayName(): string {
    if (!this.user?.role) return 'User';
    
    switch (this.user.role) {
      case 'super admin':
        return 'Super Administrator';
      case 'admin':
        return 'Administrator';
      case 'engineer':
        return 'Engineer';
      case 'shop floor':
        return 'Shop Floor';
      default:
        return 'User';
    }
  }

  private isAdminOrSuperAdmin(): boolean {
    return this.user?.role === 'admin' || this.user?.role === 'super admin';
  }

  private hasProfileChanges(): boolean {
    return JSON.stringify(this.formData) !== JSON.stringify(this.originalFormData);
  }

  private hasCompanyChanges(): boolean {
    return JSON.stringify(this.companyFormData) !== JSON.stringify(this.originalCompanyFormData);
  }

  private hasAnyChanges(): boolean {
    return this.hasProfileChanges() || (this.isAdminOrSuperAdmin() && this.hasCompanyChanges());
  }

  render() {
    if (this.isLoading && !this.formData.name) {
      return html`
        <div class="profile-page">
          <div class="loading-container">
            <sl-spinner></sl-spinner>
            <p>Loading profile...</p>
          </div>
        </div>
      `;
    }

    return html`
      <div class="profile-page">
        <div class="profile-content">
          ${this.errorMessage ? html`
            <div class="error-message">
              <sl-icon name="exclamation-triangle"></sl-icon>
              ${this.errorMessage}
            </div>
          ` : ''}

          ${this.successMessage ? html`
            <div class="success-message">
              <sl-icon name="check-circle"></sl-icon>
              ${this.successMessage}
            </div>
          ` : ''}

          <sl-tab-group placement="start">
            <sl-tab slot="nav" panel="profile">Profile</sl-tab>
            <sl-tab slot="nav" panel="address">Address</sl-tab>
            <sl-tab slot="nav" panel="account">Account</sl-tab>
            <sl-tab slot="nav" panel="notifications">
              <sl-icon slot="prefix" name="bell"></sl-icon>
              Notifications
            </sl-tab>
            ${this.isAdminOrSuperAdmin() ? html`
              <sl-tab slot="nav" panel="company">Company Settings</sl-tab>
            ` : ''}
            
            <sl-tab-panel name="profile">
              <div class="profile-form">
                <div class="form-section">
                  <div class="form-group">
                    <label for="name">Full Name *</label>
                    <sl-input
                      id="name"
                      name="name"
                      placeholder="Enter your full name"
                      value=${this.formData.name}
                      @sl-input=${(e: Event) => this.handleInputChange(e, 'name')}
                      ?invalid=${!!this.validationErrors.name}
                      size="large"
                    >
                    
                      <sl-icon name="person" slot="prefix"></sl-icon>
                    </sl-input>
                    ${this.validationErrors.name ? html`
                      <div class="error-message">${this.validationErrors.name}</div>
                    ` : ''}
                  </div>

                  <div class="form-group">
                    <label for="email">Email Address</label>
                    <sl-input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter your email address"
                      value=${this.formData.email}
                      disabled
                      size="large"
                    >
                      <sl-icon name="envelope" slot="prefix"></sl-icon>
                    </sl-input>
                    <div class="help-text">
                      Email cannot be changed as it's tied to your login credentials
                    </div>
                  </div>

                  <div class="form-group">
                    <label for="phone_number">Phone Number</label>
                    <sl-input
                      id="phone_number"
                      name="phone_number"
                      type="tel"
                      placeholder="Enter your phone number"
                      value=${this.formData.phone_number}
                      @sl-input=${(e: Event) => this.handleInputChange(e, 'phone_number')}
                      ?invalid=${!!this.validationErrors.phone_number}
                      size="large"
                    >
                      <sl-icon name="phone" slot="prefix"></sl-icon>
                    </sl-input>
                    ${this.validationErrors.phone_number ? html`
                      <div class="error-message">${this.validationErrors.phone_number}</div>
                    ` : ''}
                    <div class="help-text">
                      Optional - Enter your contact phone number
                    </div>
                  </div>
                </div>
              </div>
            </sl-tab-panel>

            <sl-tab-panel name="address">
              <div class="profile-form">
                <div class="form-section">
                  <div class="form-group">
                    <label for="address">Street Address</label>
                    <sl-input
                      id="address"
                      name="address"
                      placeholder="Enter your street address"
                      value=${this.formData.address}
                      @sl-input=${(e: Event) => this.handleInputChange(e, 'address')}
                      size="large"
                    >
                      <sl-icon name="house" slot="prefix"></sl-icon>
                    </sl-input>
                    <div class="help-text">
                      Optional - Enter your street address
                    </div>
                  </div>

                  <div class="address-row">
                    <div class="form-group">
                      <label for="city">City</label>
                      <sl-input
                        id="city"
                        name="city"
                        placeholder="Enter your city"
                        value=${this.formData.city}
                        @sl-input=${(e: Event) => this.handleInputChange(e, 'city')}
                        size="large"
                      >
                        <sl-icon name="geo-alt" slot="prefix"></sl-icon>
                      </sl-input>
                    </div>

                    <div class="form-group">
                      <label for="state">State/Province</label>
                      <sl-input
                        id="state"
                        name="state"
                        placeholder="Enter state or province"
                        value=${this.formData.state}
                        @sl-input=${(e: Event) => this.handleInputChange(e, 'state')}
                        size="large"
                      >
                        <sl-icon name="map" slot="prefix"></sl-icon>
                      </sl-input>
                    </div>
                  </div>

                  <div class="address-row">
                    <div class="form-group">
                      <label for="country">Country</label>
                      <sl-input
                        id="country"
                        name="country"
                        placeholder="Enter your country"
                        value=${this.formData.country}
                        @sl-input=${(e: Event) => this.handleInputChange(e, 'country')}
                        size="large"
                      >
                        <sl-icon name="globe" slot="prefix"></sl-icon>
                      </sl-input>
                    </div>

                    <div class="form-group">
                      <label for="zipcode">ZIP/Postal Code</label>
                      <sl-input
                        id="zipcode"
                        name="zipcode"
                        placeholder="Enter ZIP or postal code"
                        value=${this.formData.zipcode}
                        @sl-input=${(e: Event) => this.handleInputChange(e, 'zipcode')}
                        ?invalid=${!!this.validationErrors.zipcode}
                        size="large"
                      >
                        <sl-icon name="mailbox" slot="prefix"></sl-icon>
                      </sl-input>
                      ${this.validationErrors.zipcode ? html`
                        <div class="error-message">${this.validationErrors.zipcode}</div>
                      ` : ''}
                    </div>
                  </div>
                </div>
              </div>
            </sl-tab-panel>

            <sl-tab-panel name="account">
              <div class="account-info">
                <div class="form-section">
                  <h3>Account Information</h3>
                  
                  <div class="info-row">
                    <div class="info-label">Role:</div>
                    <div class="info-value">
                      <sl-badge variant="primary">${this.getRoleDisplayName()}</sl-badge>
                    </div>
                  </div>

                  <div class="info-row">
                    <div class="info-label">Company:</div>
                    <div class="info-value">${this.companyName}</div>
                  </div>

                  <div class="info-row">
                    <div class="info-label">User ID:</div>
                    <div class="info-value user-id">${this.user?.uid || 'N/A'}</div>
                  </div>

                  <div class="info-row">
                    <div class="info-label">Email:</div>
                    <div class="info-value">${this.formData.email}</div>
                  </div>

                  <div class="info-row">
                    <div class="info-label">Member Since:</div>
                    <div class="info-value">${this.getMemberSinceDate()}</div>
                  </div>
                </div>

                <div class="form-section">
                  <h3>Account Actions</h3>
                  
                  <div class="action-buttons">
                    <sl-button 
                      variant="default" 
                      size="medium"
                      @click=${this.handleChangePasswordClick}
                    >
                      <sl-icon name="key" slot="prefix"></sl-icon>
                      Change Password
                    </sl-button>
                    
                    <sl-button 
                      variant="default" 
                      size="medium"
                      disabled
                      title="Coming Soon"
                    >
                      <sl-icon name="download" slot="prefix"></sl-icon>
                      Export Data
                    </sl-button>
                    
                    <sl-button 
                      variant="danger" 
                      size="medium"
                      disabled
                      title="Coming Soon"
                    >
                      <sl-icon name="trash" slot="prefix"></sl-icon>
                      Delete Account
                    </sl-button>
                  </div>
                </div>
              </div>
            </sl-tab-panel>

            <sl-tab-panel name="notifications">
              <notification-settings></notification-settings>
            </sl-tab-panel>

            ${this.isAdminOrSuperAdmin() ? html`
              <sl-tab-panel name="company">
                <div class="profile-form">
                  <div class="form-section">
                    <h3>Company Information</h3>
                    
                    <div class="form-group">
                      <label for="company-name">Company Name *</label>
                      <sl-input
                        id="company-name"
                        name="name"
                        placeholder="Enter company name"
                        value=${this.companyFormData.name}
                        @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'name')}
                        ?invalid=${!!this.companyValidationErrors.name}
                        size="large"
                      >
                        <sl-icon name="building" slot="prefix"></sl-icon>
                      </sl-input>
                      ${this.companyValidationErrors.name ? html`
                        <div class="error-message">${this.companyValidationErrors.name}</div>
                      ` : ''}
                    </div>

                    <div class="form-group">
                      <label for="legal-name">Legal Name *</label>
                      <sl-input
                        id="legal-name"
                        name="legal_name"
                        placeholder="Enter legal company name"
                        value=${this.companyFormData.legal_name}
                        @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'legal_name')}
                        ?invalid=${!!this.companyValidationErrors.legal_name}
                        size="large"
                      >
                        <sl-icon name="file-text" slot="prefix"></sl-icon>
                      </sl-input>
                      ${this.companyValidationErrors.legal_name ? html`
                        <div class="error-message">${this.companyValidationErrors.legal_name}</div>
                      ` : ''}
                    </div>

                    <div class="form-group">
                      <label for="industry">Industry *</label>
                      <sl-input
                        id="industry"
                        name="industry"
                        placeholder="Enter industry"
                        value=${this.companyFormData.industry}
                        @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'industry')}
                        ?invalid=${!!this.companyValidationErrors.industry}
                        size="large"
                      >
                        <sl-icon name="briefcase" slot="prefix"></sl-icon>
                      </sl-input>
                      ${this.companyValidationErrors.industry ? html`
                        <div class="error-message">${this.companyValidationErrors.industry}</div>
                      ` : ''}
                    </div>

                    <div class="address-row">
                      <div class="form-group">
                        <label for="company-size">Company Size</label>
                        <sl-input
                          id="company-size"
                          name="company_size"
                          type="number"
                          placeholder="Number of employees"
                          value=${this.companyFormData.company_size}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'company_size')}
                          ?invalid=${!!this.companyValidationErrors.company_size}
                          size="large"
                        >
                          <sl-icon name="users" slot="prefix"></sl-icon>
                        </sl-input>
                        ${this.companyValidationErrors.company_size ? html`
                          <div class="error-message">${this.companyValidationErrors.company_size}</div>
                        ` : ''}
                      </div>

                      <div class="form-group">
                        <label for="founded-year">Founded Year</label>
                        <sl-input
                          id="founded-year"
                          name="founded_year"
                          type="number"
                          placeholder="Year founded"
                          value=${this.companyFormData.founded_year || ''}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'founded_year')}
                          ?invalid=${!!this.companyValidationErrors.founded_year}
                          size="large"
                        >
                          <sl-icon name="calendar" slot="prefix"></sl-icon>
                        </sl-input>
                        ${this.companyValidationErrors.founded_year ? html`
                          <div class="error-message">${this.companyValidationErrors.founded_year}</div>
                        ` : ''}
                      </div>
                    </div>
                  </div>

                  <div class="form-section">
                    <h3>Contact Information</h3>
                    
                    <div class="form-group">
                      <label for="company-email">Email Address *</label>
                      <sl-input
                        id="company-email"
                        name="email_address"
                        type="email"
                        placeholder="Enter company email"
                        value=${this.companyFormData.email_address}
                        @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'email_address')}
                        ?invalid=${!!this.companyValidationErrors.email_address}
                        size="large"
                      >
                        <sl-icon name="envelope" slot="prefix"></sl-icon>
                      </sl-input>
                      ${this.companyValidationErrors.email_address ? html`
                        <div class="error-message">${this.companyValidationErrors.email_address}</div>
                      ` : ''}
                    </div>

                    <div class="address-row">
                      <div class="form-group">
                        <label for="company-phone">Phone Number *</label>
                        <sl-input
                          id="company-phone"
                          name="phone_number"
                          placeholder="Enter phone number"
                          value=${this.companyFormData.phone_number}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'phone_number')}
                          ?invalid=${!!this.companyValidationErrors.phone_number}
                          size="large"
                        >
                          <sl-icon name="phone" slot="prefix"></sl-icon>
                        </sl-input>
                        ${this.companyValidationErrors.phone_number ? html`
                          <div class="error-message">${this.companyValidationErrors.phone_number}</div>
                        ` : ''}
                      </div>

                      <div class="form-group">
                        <label for="whatsapp-number">WhatsApp Number</label>
                        <sl-input
                          id="whatsapp-number"
                          name="whatsapp_number"
                          placeholder="Enter WhatsApp number"
                          value=${this.companyFormData.whatsapp_number || ''}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'whatsapp_number')}
                          size="large"
                        >
                          <sl-icon name="message-circle" slot="prefix"></sl-icon>
                        </sl-input>
                      </div>
                    </div>

                    <div class="form-group">
                      <label for="website-url">Website URL</label>
                      <sl-input
                        id="website-url"
                        name="website_url"
                        placeholder="Enter website URL"
                        value=${this.companyFormData.website_url || ''}
                        @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'website_url')}
                        size="large"
                      >
                        <sl-icon name="globe" slot="prefix"></sl-icon>
                      </sl-input>
                    </div>
                  </div>

                  <div class="form-section">
                    <h3>Address Information</h3>
                    
                    <div class="form-group">
                      <label for="street-name">Street Name *</label>
                      <sl-input
                        id="street-name"
                        name="street_name"
                        placeholder="Enter street name"
                        value=${this.companyFormData.street_name}
                        @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'street_name')}
                        ?invalid=${!!this.companyValidationErrors.street_name}
                        size="large"
                      >
                        <sl-icon name="map-pin" slot="prefix"></sl-icon>
                      </sl-input>
                      ${this.companyValidationErrors.street_name ? html`
                        <div class="error-message">${this.companyValidationErrors.street_name}</div>
                      ` : ''}
                    </div>

                    <div class="address-row">
                      <div class="form-group">
                        <label for="house-number">House Number *</label>
                        <sl-input
                          id="house-number"
                          name="house_number"
                          placeholder="Enter house number"
                          value=${this.companyFormData.house_number}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'house_number')}
                          ?invalid=${!!this.companyValidationErrors.house_number}
                          size="large"
                        >
                          <sl-icon name="hash" slot="prefix"></sl-icon>
                        </sl-input>
                        ${this.companyValidationErrors.house_number ? html`
                          <div class="error-message">${this.companyValidationErrors.house_number}</div>
                        ` : ''}
                      </div>

                      <div class="form-group">
                        <label for="house-addition">House Number Addition</label>
                        <sl-input
                          id="house-addition"
                          name="house_number_addition"
                          placeholder="Enter house number addition"
                          value=${this.companyFormData.house_number_addition || ''}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'house_number_addition')}
                          size="large"
                        >
                          <sl-icon name="plus" slot="prefix"></sl-icon>
                        </sl-input>
                      </div>
                    </div>

                    <div class="address-row">
                      <div class="form-group">
                        <label for="company-zipcode">Zipcode *</label>
                        <sl-input
                          id="company-zipcode"
                          name="zipcode"
                          placeholder="Enter zipcode"
                          value=${this.companyFormData.zipcode}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'zipcode')}
                          ?invalid=${!!this.companyValidationErrors.zipcode}
                          size="large"
                        >
                          <sl-icon name="map" slot="prefix"></sl-icon>
                        </sl-input>
                        ${this.companyValidationErrors.zipcode ? html`
                          <div class="error-message">${this.companyValidationErrors.zipcode}</div>
                        ` : ''}
                      </div>

                      <div class="form-group">
                        <label for="company-city">City *</label>
                        <sl-input
                          id="company-city"
                          name="city"
                          placeholder="Enter city"
                          value=${this.companyFormData.city}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'city')}
                          ?invalid=${!!this.companyValidationErrors.city}
                          size="large"
                        >
                          <sl-icon name="map-pin" slot="prefix"></sl-icon>
                        </sl-input>
                        ${this.companyValidationErrors.city ? html`
                          <div class="error-message">${this.companyValidationErrors.city}</div>
                        ` : ''}
                      </div>
                    </div>

                    <div class="address-row">
                      <div class="form-group">
                        <label for="state-province">State/Province *</label>
                        <sl-input
                          id="state-province"
                          name="state_province"
                          placeholder="Enter state/province"
                          value=${this.companyFormData.state_province}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'state_province')}
                          ?invalid=${!!this.companyValidationErrors.state_province}
                          size="large"
                        >
                          <sl-icon name="map" slot="prefix"></sl-icon>
                        </sl-input>
                        ${this.companyValidationErrors.state_province ? html`
                          <div class="error-message">${this.companyValidationErrors.state_province}</div>
                        ` : ''}
                      </div>

                      <div class="form-group">
                        <label for="company-country">Country *</label>
                        <sl-input
                          id="company-country"
                          name="country"
                          placeholder="Enter country"
                          value=${this.companyFormData.country}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'country')}
                          ?invalid=${!!this.companyValidationErrors.country}
                          size="large"
                        >
                          <sl-icon name="flag" slot="prefix"></sl-icon>
                        </sl-input>
                        ${this.companyValidationErrors.country ? html`
                          <div class="error-message">${this.companyValidationErrors.country}</div>
                        ` : ''}
                      </div>
                    </div>
                  </div>

                  <div class="form-section">
                    <h3>Business Settings</h3>
                    
                    <div class="form-group">
                      <label for="billing-email">Billing Email *</label>
                      <sl-input
                        id="billing-email"
                        name="billing_email"
                        type="email"
                        placeholder="Enter billing email"
                        value=${this.companyFormData.billing_email}
                        @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'billing_email')}
                        ?invalid=${!!this.companyValidationErrors.billing_email}
                        size="large"
                      >
                        <sl-icon name="credit-card" slot="prefix"></sl-icon>
                      </sl-input>
                      ${this.companyValidationErrors.billing_email ? html`
                        <div class="error-message">${this.companyValidationErrors.billing_email}</div>
                      ` : ''}
                    </div>

                    <div class="address-row">
                      <div class="form-group">
                        <label for="billing-cycle">Billing Cycle</label>
                        <sl-select
                          id="billing-cycle"
                          name="billing_cycle"
                          value=${this.companyFormData.billing_cycle}
                          @sl-change=${(e: Event) => this.handleCompanyInputChange(e, 'billing_cycle')}
                          size="large"
                        >
                          <sl-option value="Monthly">Monthly</sl-option>
                          <sl-option value="Yearly">Yearly</sl-option>
                        </sl-select>
                      </div>

                      <div class="form-group">
                        <label for="currency">Currency</label>
                        <sl-select
                          id="currency"
                          name="currency"
                          value=${this.companyFormData.currency}
                          @sl-change=${(e: Event) => this.handleCompanyInputChange(e, 'currency')}
                          size="large"
                        >
                          <sl-option value="EUR">EUR</sl-option>
                          <sl-option value="USD">USD</sl-option>
                          <sl-option value="GBP">GBP</sl-option>
                        </sl-select>
                      </div>
                    </div>

                    <div class="address-row">
                      <div class="form-group">
                        <label for="timezone">Timezone</label>
                        <sl-select
                          id="timezone"
                          name="timezone"
                          value=${this.companyFormData.timezone}
                          @sl-change=${(e: Event) => this.handleCompanyInputChange(e, 'timezone')}
                          size="large"
                        >
                          <sl-option value="Europe/Amsterdam">Europe/Amsterdam</sl-option>
                          <sl-option value="Europe/London">Europe/London</sl-option>
                          <sl-option value="America/New_York">America/New_York</sl-option>
                          <sl-option value="America/Los_Angeles">America/Los_Angeles</sl-option>
                        </sl-select>
                      </div>

                      <div class="form-group">
                        <label for="language">Language</label>
                        <sl-select
                          id="language"
                          name="language"
                          value=${this.companyFormData.language}
                          @sl-change=${(e: Event) => this.handleCompanyInputChange(e, 'language')}
                          size="large"
                        >
                          <sl-option value="nl">Dutch</sl-option>
                          <sl-option value="en">English</sl-option>
                          <sl-option value="de">German</sl-option>
                          <sl-option value="fr">French</sl-option>
                        </sl-select>
                      </div>
                    </div>

                    <div class="address-row">
                      <div class="form-group">
                        <label for="work-hours-start">Work Hours Start</label>
                        <sl-input
                          id="work-hours-start"
                          name="work_hours_start"
                          type="time"
                          value=${this.companyFormData.work_hours_start}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'work_hours_start')}
                          size="large"
                        >
                          <sl-icon name="clock" slot="prefix"></sl-icon>
                        </sl-input>
                      </div>

                      <div class="form-group">
                        <label for="work-hours-end">Work Hours End</label>
                        <sl-input
                          id="work-hours-end"
                          name="work_hours_end"
                          type="time"
                          value=${this.companyFormData.work_hours_end}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'work_hours_end')}
                          size="large"
                        >
                          <sl-icon name="clock" slot="prefix"></sl-icon>
                        </sl-input>
                      </div>
                    </div>

                    <div class="form-group">
                      <label for="default-priority">Default Priority</label>
                      <sl-select
                        id="default-priority"
                        name="default_priority"
                        value=${this.companyFormData.default_priority}
                        @sl-change=${(e: Event) => this.handleCompanyInputChange(e, 'default_priority')}
                        size="large"
                      >
                        <sl-option value="Low">Low</sl-option>
                        <sl-option value="Medium">Medium</sl-option>
                        <sl-option value="High">High</sl-option>
                        <sl-option value="Critical">Critical</sl-option>
                      </sl-select>
                    </div>

                    <div class="form-group">
                      <sl-checkbox
                        name="auto_assign_enabled"
                        ?checked=${this.companyFormData.auto_assign_enabled}
                        @sl-change=${(e: Event) => this.handleCompanyInputChange(e, 'auto_assign_enabled')}
                      >
                        Enable automatic work order assignment
                      </sl-checkbox>
                    </div>
                  </div>

                  <div class="form-section">
                    <h3>Financial Information</h3>
                    
                    <div class="address-row">
                      <div class="form-group">
                        <label for="vat-number">VAT Number</label>
                        <sl-input
                          id="vat-number"
                          name="vat_number"
                          placeholder="Enter VAT number"
                          value=${this.companyFormData.vat_number || ''}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'vat_number')}
                          size="large"
                        >
                          <sl-icon name="receipt" slot="prefix"></sl-icon>
                        </sl-input>
                      </div>

                      <div class="form-group">
                        <label for="tax-id">Tax ID</label>
                        <sl-input
                          id="tax-id"
                          name="tax_id"
                          placeholder="Enter tax ID"
                          value=${this.companyFormData.tax_id || ''}
                          @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'tax_id')}
                          size="large"
                        >
                          <sl-icon name="file-text" slot="prefix"></sl-icon>
                        </sl-input>
                      </div>
                    </div>

                    <div class="form-group">
                      <label for="iban">IBAN</label>
                      <sl-input
                        id="iban"
                        name="iban"
                        placeholder="Enter IBAN"
                        value=${this.companyFormData.iban || ''}
                        @sl-input=${(e: Event) => this.handleCompanyInputChange(e, 'iban')}
                        size="large"
                      >
                        <sl-icon name="credit-card" slot="prefix"></sl-icon>
                      </sl-input>
                    </div>
                  </div>

                </div>
              </sl-tab-panel>
            ` : ''}
          </sl-tab-group>

          ${this.showChangePassword ? html`
            <sl-dialog label="Change Password" open @sl-hide=${this.handlePasswordCancel}>
              <div class="password-form">
                <div class="form-group">
                  <label for="currentPassword">Current Password *</label>
                  <sl-input
                    id="currentPassword"
                    type="password"
                    placeholder="Enter your current password"
                    value=${this.passwordData.currentPassword}
                    @sl-input=${(e: Event) => this.handlePasswordInputChange(e, 'currentPassword')}
                    ?invalid=${!!this.validationErrors.currentPassword}
                    size="large"
                  >
                    <sl-icon name="lock" slot="prefix"></sl-icon>
                  </sl-input>
                  ${this.validationErrors.currentPassword ? html`
                    <div class="error-message">${this.validationErrors.currentPassword}</div>
                  ` : ''}
                </div>

                <div class="form-group">
                  <label for="newPassword">New Password *</label>
                  <sl-input
                    id="newPassword"
                    type="password"
                    placeholder="Enter your new password"
                    value=${this.passwordData.newPassword}
                    @sl-input=${(e: Event) => this.handlePasswordInputChange(e, 'newPassword')}
                    ?invalid=${!!this.validationErrors.newPassword}
                    size="large"
                  >
                    <sl-icon name="key" slot="prefix"></sl-icon>
                  </sl-input>
                  ${this.validationErrors.newPassword ? html`
                    <div class="error-message">${this.validationErrors.newPassword}</div>
                  ` : ''}
                  <div class="help-text">
                    Password must be at least 6 characters long
                  </div>
                </div>

                <div class="form-group">
                  <label for="confirmPassword">Confirm New Password *</label>
                  <sl-input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your new password"
                    value=${this.passwordData.confirmPassword}
                    @sl-input=${(e: Event) => this.handlePasswordInputChange(e, 'confirmPassword')}
                    ?invalid=${!!this.validationErrors.confirmPassword}
                    size="large"
                  >
                    <sl-icon name="key" slot="prefix"></sl-icon>
                  </sl-input>
                  ${this.validationErrors.confirmPassword ? html`
                    <div class="error-message">${this.validationErrors.confirmPassword}</div>
                  ` : ''}
                </div>
              </div>

              <div slot="footer" class="dialog-footer">
                <sl-button 
                  variant="neutral" 
                  @click=${this.handlePasswordCancel}
                  ?disabled=${this.isLoading}
                >
                  Cancel
                </sl-button>
                <sl-button 
                  variant="primary" 
                  @click=${this.handlePasswordSave}
                  ?loading=${this.isLoading}
                  ?disabled=${this.isLoading}
                >
                  ${this.isLoading ? 'Updating...' : 'Update Password'}
                </sl-button>
              </div>
            </sl-dialog>
          ` : ''}

          <div class="profile-actions">
            <sl-button
              variant="primary"
              size="large"
              @click=${this.handleSave}
              ?loading=${this.isLoading}
              ?disabled=${this.isLoading || !this.hasAnyChanges()}
            >
              <sl-icon name="check" slot="prefix"></sl-icon>
              ${this.isLoading ? 'Saving...' : 'Save Changes'}
            </sl-button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'profile-page': ProfilePage;
  }
}
